import moment from 'moment'
import {
  Button,
  Checkbox,
  Modal,
  Popconfirm,
  Tooltip,
  Tag,
  message,
} from 'antd'
import styled from 'styled-components'
import React from 'react'
import { useParams } from 'react-router-dom'
import InvoiceDiscrepancyAction from '../invoices/InvoiceDiscrepancyAction'
import { useCanAccess } from 'src/hooks/useIsBlocked'
import { API } from 'src/utils/interceptor'
import {
  CREDIT_REQUEST_DISPLAY,
  canChaseCreditRequest,
  creditChaseSummary,
  formatCreditRequestAmountCell,
} from 'src/utils/creditRequestTableHelpers'

const truncateRender = (display, full, emptyTooltip) => {
  if (!full) {
    return (
      <Tooltip title={emptyTooltip || undefined}>
        <Muted>{display}</Muted>
      </Tooltip>
    )
  }
  return (
    <Tooltip title={full}>
      <span>{display}</span>
    </Tooltip>
  )
}

function InvoiceCreditLink({ record }) {
  const { venueId } = useParams()
  const canSeeInvoices = useCanAccess('invoices')
  if (!canSeeInvoices)
    return <Muted>Invoice access required to review credit.</Muted>
  return (
    <InvoiceDiscrepancyAction
      venueId={venueId}
      invoice={
        record.invoice || {
          id: record.invoiceId,
          purchaseOrderNumber: record.PON,
        }
      }
    />
  )
}

const CreditNotesColumns = (
  makeOrderResolvedValue,
  { venueId: chaseVenueId, chaseById = {}, onChased } = {},
) => {
  return [
    {
      title: 'PO Number',
      dataIndex: 'PON',
      key: 'PON',
      width: 130,
      fixed: 'left',
    },
    {
      title: 'SKU',
      dataIndex: 'SKU',
      key: 'SKU',
      width: 100,
      render: (sku, record) =>
        sku === CREDIT_REQUEST_DISPLAY.unknownSku ? (
          <Tooltip title={CREDIT_REQUEST_DISPLAY.blankIdentityTooltip}>
            <Muted>{sku}</Muted>
          </Tooltip>
        ) : (
          <Tooltip
            title={
              record.identityFallback
                ? CREDIT_REQUEST_DISPLAY.blankIdentityTooltip
                : undefined
            }
          >
            <span>{sku}</span>
          </Tooltip>
        ),
    },
    {
      title: 'Product',
      dataIndex: 'productTitle',
      key: 'product_title',
      width: 180,
      ellipsis: true,
      render: (title, record) =>
        title === CREDIT_REQUEST_DISPLAY.unknownProduct ? (
          <Tooltip title={CREDIT_REQUEST_DISPLAY.blankIdentityTooltip}>
            <Muted>{title}</Muted>
          </Tooltip>
        ) : (
          <Tooltip
            title={
              record.identityFallback
                ? `${title} — ${CREDIT_REQUEST_DISPLAY.blankIdentityTooltip}`
                : title
            }
          >
            <span>{title}</span>
          </Tooltip>
        ),
    },
    {
      title: 'Supplier',
      dataIndex: 'supplierTitle',
      key: 'supplier_title',
      width: 120,
      sorter: (a, b) =>
        String(a.supplierTitle)
          .trim()
          .split(' ')
          .join('')
          .localeCompare(String(b.supplierTitle).trim().split(' ').join('')),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      width: 110,
      render: reason =>
        reason === CREDIT_REQUEST_DISPLAY.unknownReason ? (
          <Muted>{reason}</Muted>
        ) : (
          reason
        ),
    },
    {
      title: 'Notes',
      dataIndex: 'notesDisplay',
      key: 'notes',
      width: 140,
      render: (display, record) =>
        truncateRender(
          display,
          record.hasNotes ? record.notesFull : '',
          'No notes on this request',
        ),
    },
    {
      title: 'Ordered → credit qty',
      dataIndex: 'qtyDisplay',
      key: 'ordered_credit_qty',
      width: 120,
    },
    {
      title: 'Est. credit',
      dataIndex: 'amount',
      key: 'credit_amount',
      width: 90,
      render: (amount, record) =>
        record.amountMissing ||
        amount === CREDIT_REQUEST_DISPLAY.unknownAmount ? (
          <Tooltip title={CREDIT_REQUEST_DISPLAY.missingAmountTooltip}>
            <Muted>{CREDIT_REQUEST_DISPLAY.unknownAmount}</Muted>
          </Tooltip>
        ) : record.invoiceId ? (
          <Tooltip title='Amount requested for the whole invoice, not this product line. Do not add repeated invoice amounts together.'>
            {formatCreditRequestAmountCell(amount)}
          </Tooltip>
        ) : (
          formatCreditRequestAmountCell(amount)
        ),
    },
    {
      title: 'Status',
      key: 'credit_supplierStatus',
      width: 180,
      render: (_, record) => {
        const chips =
          Array.isArray(record.lifecycleChips) && record.lifecycleChips.length
            ? record.lifecycleChips
            : [
                {
                  key: 'stage',
                  label: record.statusText,
                  color: record.statusColor || 'default',
                },
              ]
        const hint = record.followUpHint
        return (
          <StatusCell>
            <ChipRow>
              {chips.map(chip => (
                <Tooltip
                  key={chip.key}
                  title={
                    chip.key === 'stage' ? record.statusTooltip : undefined
                  }
                >
                  <Tag color={chip.color}>{chip.label}</Tag>
                </Tooltip>
              ))}
            </ChipRow>
            {hint ? (
              <Tooltip title={hint.tooltip}>
                <HintText>{hint.text}</HintText>
              </Tooltip>
            ) : null}
          </StatusCell>
        )
      },
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 100,
    },
    {
      title: 'Requested by',
      dataIndex: 'requestedBy',
      key: 'requested_by',
      width: 120,
      render: (value, record) =>
        value ? (
          value
        ) : (
          <Tooltip title='Requester wasn’t recorded on older asks — Source still shows how it was raised.'>
            <Muted>{record.source || '—'}</Muted>
          </Tooltip>
        ),
    },
    {
      title: 'Requested',
      dataIndex: 'requestedAt',
      key: 'requested_at',
      width: 110,
      sorter: (a, b) =>
        moment(a.requestedAt || 0).unix() - moment(b.requestedAt || 0).unix(),
      sortDirections: ['ascend', 'descend'],
      render: dateTime =>
        dateTime ? moment(dateTime).format('DD-MM-YYYY') : '—',
    },
    {
      title: 'Ordered Date',
      dataIndex: 'createdAt',
      key: 'dateOfOrder',
      width: 110,
      sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
      sortDirections: ['ascend', 'descend'],
      render: dateTime => {
        const date = moment(dateTime).format('DD-MM-YYYY')
        return date
      },
    },
    {
      title: 'Chase',
      key: 'chase',
      width: 280,
      fixed: 'right',
      render: (_, record) => {
        if (!canChaseCreditRequest(record)) {
          if (record.statusText === 'Supplier approved' && !record.resolved) {
            return <Muted>Next step is the credit note.</Muted>
          }
          return null
        }
        const chase = chaseById[String(record.receiptingId)]
        const summary = creditChaseSummary(chase)
        const send = () => {
          const supplier = record.supplierTitle || 'the supplier'
          Modal.confirm({
            title: 'Chase this credit request?',
            content: `We'll send a reminder to ${supplier} regarding this outstanding credit request. Another reminder cannot go until tomorrow.`,
            okText: 'Send Chaser',
            cancelText: 'Cancel',
            onOk: async () => {
              try {
                const result = await API.post(
                  'api2',
                  `receipting/${chaseVenueId}/credit-requests/${record.receiptingId}/chase`,
                  { body: {} },
                )
                if (onChased) onChased(record.receiptingId, result?.creditChase)
                message.success(`Reminder sent to ${supplier}.`)
              } catch (error) {
                const chaseRecord = error?.response?.data?.creditChase
                if (chaseRecord && onChased) {
                  onChased(record.receiptingId, chaseRecord)
                }
                message.error(
                  error?.response?.data?.message ||
                    'That reminder could not be sent.',
                )
              }
            },
          })
        }
        return (
          <div>
            {summary.label ? (
              <Muted style={{ whiteSpace: 'nowrap' }}>{summary.label}</Muted>
            ) : null}
            {summary.chasedToday ? null : (
              <Button type='link' onClick={send} style={{ paddingLeft: 0 }}>
                Chase Supplier
              </Button>
            )}
          </div>
        )
      },
    },
    {
      title: 'Resolved',
      dataIndex: 'resolved',
      key: 'checkbox_resolved',
      width: 90,
      fixed: 'right',
      render: (value, obj) => {
        if (obj.invoiceId) return <InvoiceCreditLink record={obj} />
        return (
          <ActionWrapper>
            <CardButton
              title={
                obj.creditNoteApplied
                  ? 'Credit note is on this PO. Mark this Ask-for-credit request resolved so it leaves your outstanding alert?'
                  : 'Mark resolved if the supplier replaced goods, you no longer need the credit, or you’ve finished chasing — this clears it from the amber badge.'
              }
              placement='topRight'
              onConfirm={() => makeOrderResolvedValue(obj.receiptingId)}
              okText='Yes, mark resolved'
              cancelText='Keep open'
              hidden={value}
            >
              <div onClick={e => e.stopPropagation()}>
                <Checkbox
                  className='checkbox'
                  checked={value}
                  disabled={value}
                />
              </div>
            </CardButton>
          </ActionWrapper>
        )
      },
    },
  ]
}

const ActionWrapper = styled.div`
  display: flex;
  justify-content: center;
`

const CardButton = styled(Popconfirm)``

const StatusCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const HintText = styled.span`
  font-size: 11px;
  line-height: 1.3;
  color: #8c8c8c;
`

const Muted = styled.span`
  color: #8c8c8c;
`

export default CreditNotesColumns
